import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Spinner,
  Tab,
  TabList,
  Tabs,
  Tag,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ArrowsClockwise, CaretDoubleRight, CaretDown, CaretLeft, CaretRight, ChartBar, ClockCounterClockwise, Eye, FileArrowUp, FileCsv, FileText, FlagCheckered, Lock, MagicWand, PencilSimple, Plus, Sparkle } from "@phosphor-icons/react";
import { fetchCharts, fetchDataFrameData, fetchDataFrameList } from "../api/dataMappingClient";
import { useChat } from "../state/useChat";
import { useSession } from "../state/useSession";
import { useWorkingPanel } from "../state/useWorkingPanel";
import { useFileUpload } from "../hooks/useFileUpload";
import { DataPreviewTable } from "./DataPreviewTable";
import { FoundationCoverageStrip } from "./FoundationCoverageStrip";
import { ChartsBoardView } from "./charts/ChartsBoardView";
import { isOutputKey } from "./outputFrames";
import { CommitStage } from "./CommitStage";
import WorkingDataHistoryPanel from "./WorkingDataHistoryPanel";
import CommitHistoryPanel from "./CommitHistoryPanel";
import { STAGE_CHARTS, STAGE_COMMIT, STAGE_LIVE, STAGE_SOURCE, STAGE_WORKING } from "./stages";
import { FoundationSavePanel } from "./FoundationSavePanel";
import { useCommitStatus } from "../hooks/useCommitStatus";
import useAirUserRoles from "../../../utils/AuthUtils";
import { useRightSidebar } from "context/RightSidebarContext";
import { formatTarget } from "./taskLabels";

// The AI builds task-mapped results as dataframes named with "_output" (e.g. "mapped_output", then renamed
// to "fixed_assets_output" when it moves on to the next mapping). These frames belong in the Live
// ("Map & review") view, not the Working/Source tabs — see ./outputFrames, which holds that rule now
// because the websocket hook needs it too, to know when to bring the user here.

// Charts are hidden. A flag rather than a deletion because nothing here is broken: the board, the
// renderers and the chart specs on ds_session_chart all still work, so this is a decision about what the
// panel shows and it should be cheap to revisit.
//
// Three things move together, and this is only one of them — the agent's create_chart/delete_chart are
// unwired from TOOLS and the `## Charts` prompt section is gone (see data-sorting/CLAUDE.md). Gating the
// render alone would not have worked, because this board is the only place a chart can be SEEN: a
// `chart:ref` fence renders ChartRefCard, a button reading "View on Charts board" whose click reopens this
// view. Leaving the tools live would have had the agent making artefacts nobody could open and then
// pointing at a tab that no longer exists.
//
// The auto-focus and the fetch are gated with the render for the same reason: a chart created by the agent
// pulls focus here (chartCreatedCounter below), which with no tab to click would strand the panel on a
// view the user can neither see nor leave.
const SHOW_CHARTS = false;

// The destination task's own lookup tables (allowed values -> ids, permitted measure pairs, dimension
// parent/child), which the agent joins against instead of copying ids out of its context window.
//
// Keyed on `origin`, NOT on the "task_" name prefix the backend happens to use: a user is free to name
// their own frame "task_notes", and it must not be filed under the task's reference data. The backend
// sets this origin in app/services/task_reference.py.
const isReferenceFrame = (df) => df.origin === "task_reference";

export function WorkingPanel({ sessionId, onOpenUpload, onOpenLibrary, onUploaded, onRemap }) {
  const { isESGAdmin } = useAirUserRoles();
  const { hasFile, sessionKind, aiIngestTarget, hasTaskTemplate, sourceFileName } = useSession();
  const { isConnected, isTurnActive } = useChat();
  const { setRightSidebar, doOpen } = useRightSidebar();
  // A Foundation Data v2 curation session. Read from the SESSION rather than from the URL, so a
  // hand-edited link cannot put the carbon commit panel in front of a foundation session.
  const isFoundation = sessionKind === "foundation_v2";
  // The pipeline tabs stay locked until there is source data to work on. "Source data" is an uploaded
  // file for a carbon session, but a foundation session never has one: its rows are PUSHED in at
  // creation (POST /import-rows, origin "foundation_v2") and only /upload ever sets source_file_path,
  // so `hasFile` stays false for that session's whole life. Gating on it alone would leave Save — the
  // entire point of a foundation session — permanently disabled behind "Upload source data first."
  const hasSourceData = hasFile || isFoundation;
  // Does the session know what it is mapping onto? A deep link from a task's grid carries the target; a
  // session that agreed one in conversation has read the template instead. With neither, the automatic
  // mapping deliberately does not run, so the Live stage has to say why rather than sit there empty.
  const hasDestination = Boolean(aiIngestTarget) || Boolean(hasTaskTemplate);
  const {
    // Which stage is showing. Shared state (the `workingPanel` slice) rather than local to this component,
    // because two other places need it: ChatPanel closes the composer on the read-only stages, and
    // SessionLayout starts the AI mapping when the user arrives at Map & review. See ./stages for the
    // vocabulary and for which stages are read-only.
    stage,
    setStage,
    dataframes,
    selectedKey,
    setSelectedKey,
    setDataframes,
    tableData,
    setTableData,
    currentPage,
    totalPages,
    setPage,
    setTotalPages,
    isLoadingList,
    setLoadingList,
    isLoadingData,
    setLoadingData,
    refreshCounter,
    selectionMode,
    selectedColumns,
    selectedRows,
    selectedCells,
    selectColumn,
    selectRow,
    selectCell,
    clearSelection,
    charts,
    setCharts,
    isLoadingCharts,
    setLoadingCharts,
    chartsRefreshCounter,
    outputCreatedCounter,
    chartCreatedCounter,
    refreshCharts,
    selectedOutputKey,
    setSelectedOutputKey,
  } = useWorkingPanel();

  const [pageInput, setPageInput] = useState("1");
  // Collapsed by default: these are the task's lookup tables, useful to inspect occasionally but not
  // what the user came to look at.
  const [showReference, setShowReference] = useState(false);

  // The backend emits a "mapped_output" dataframe — the result of mapping the
  // working data onto the destination task. It belongs in the Live view
  // ("Map & review"), not in the Working-data tab strip.
  // Split the session's frames between the Working/Source stages (raw and
  // in-progress data) and the Live stage (the AI's "_output" mapped results).
  // Reference frames are excluded from BOTH stages: they are neither the user's working data nor a
  // mapped result, and leaving them in the Working strip pushes the user's own tabs off to the right
  // (one org can contribute five or more of them).
  const workingDataframes = dataframes.filter(
    (d) => !isOutputKey(d.key) && !isReferenceFrame(d)
  );
  const outputDataframes = dataframes.filter((d) => isOutputKey(d.key) && !isReferenceFrame(d));
  const referenceDataframes = dataframes.filter(isReferenceFrame);
  const referenceSelected = referenceDataframes.some((d) => d.key === selectedKey);

  // The Commit tab's own "Up to date" / "Changed" chip and disabled state — read here (not just inside
  // CommitStage) so the nav reflects it even while the user is looking at a different stage.
  const { isUpToDate: isCommitUpToDate, navChip: commitNavChip } = useCommitStatus({
    sessionId,
    selectedOutputKey,
    outputCreatedCounter,
  });

  /*
    The fingerprint of the frame in view, which is what the coverage strip refetches on. Deliberately not
    rows x columns: the commonest transform here rewrites cells and leaves the shape identical, so a
    shape-keyed refresh would sit still through exactly the changes that move the numbers.

    Not shown for a reference frame — the wanted-datapoints list has no coverage of its own, and asking how
    much of the structure the structure covers is a question with no meaning.
  */
  const selectedFrame = dataframes.find((d) => d.key === selectedKey);
  const showCoverage = isFoundation && !!selectedKey && !referenceSelected;

  // A carbon session's Preview and Commit stages share one body (MappedStage). A foundation session has
  // no Preview tab, and its Commit stage is the foundation Save panel instead.
  const isMappedStage = !isFoundation && (stage === STAGE_LIVE || stage === STAGE_COMMIT);

  // Keep the reviewed-output selection valid as the chat creates, renames and deletes frames. Lives here
  // rather than in OutputFrameTabs, which only renders when there is more than one output: the commit
  // panel commits `selectedOutputKey`, and a stale or null key there is the difference between
  // committing what was reviewed and committing nothing.
  useEffect(() => {
    if (outputDataframes.length === 0) {
      if (selectedOutputKey !== null) setSelectedOutputKey(null);
      return;
    }
    if (!outputDataframes.some((o) => o.key === selectedOutputKey)) {
      setSelectedOutputKey(outputDataframes[0].key);
    }
  }, [outputDataframes, selectedOutputKey, setSelectedOutputKey]);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const list = await fetchDataFrameList(sessionId);
      setDataframes(list);
      if (!selectedKey && list.length > 0) {
        // Default to a working dataframe; "_output" frames live in the Live view, and the task's
        // reference tables are not the user's data — landing on one would show a list of the
        // destination's allowed fuel types as though it were the spreadsheet they just uploaded.
        const source = list.find((d) => d.key === "source");
        const firstWorking = list.find((d) => !isOutputKey(d.key) && !isReferenceFrame(d));
        setSelectedKey(source ? "source" : (firstWorking ?? list[0]).key);
      }
    } catch {
      setDataframes([]);
    } finally {
      setLoadingList(false);
    }
  }, [sessionId, setDataframes, setSelectedKey, setLoadingList, selectedKey]);

  const loadData = useCallback(
    async (page, key) => {
      const targetKey = key ?? selectedKey;
      const targetPage = page ?? currentPage;
      if (!targetKey) {
        setTableData(null);
        return;
      }
      setLoadingData(true);
      try {
        const data = await fetchDataFrameData(sessionId, targetKey, targetPage);
        setTableData(data);
        setTotalPages(data.total_pages);
      } catch {
        setTableData(null);
      } finally {
        setLoadingData(false);
      }
    },
    [sessionId, selectedKey, currentPage, setTableData, setLoadingData, setTotalPages]
  );

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (selectedKey) loadData(1, selectedKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  useEffect(() => {
    if (refreshCounter === 0) return;
    loadList().then(() => loadData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshCounter]);

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  // Fetched independent of `stage` (not just when the Charts tab is active) so
  // the tab's count badge stays accurate even while the user is looking at
  // another stage. chartsRefreshCounter bumps on every data_modified event
  // (useDataMappingWebSocket.js) — that's the entire live-recompute path;
  // there is no separate invalidate/staleness step anywhere else.
  useEffect(() => {
    if (!SHOW_CHARTS) return undefined;
    let cancelled = false;
    setLoadingCharts(true);
    fetchCharts(sessionId)
      .then((list) => {
        if (!cancelled) setCharts(list);
      })
      .catch(() => {
        if (!cancelled) setCharts([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCharts(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, chartsRefreshCounter]);

  // A brand-new chart (not just a refresh of existing ones) pulls focus to
  // the Charts board — chartCreatedCounter only bumps on CHART_CREATED, never
  // on a plain REFRESH_CHARTS, so a background recompute never steals focus
  // from a table the user is actively reading.
  useEffect(() => {
    if (!SHOW_CHARTS) return;
    if (chartCreatedCounter === 0) return;
    setStage(STAGE_CHARTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartCreatedCounter]);

  // The agent has built the frame the user is meant to review, so bring them to it. Without this the
  // output appears as a tab nobody is looking at: the user stays on Working data, and the one screen whose
  // purpose is checking the rows before they are written has to be found.
  //
  // Driven by the counter and not by "an output frame exists", which matters on a resumed session:
  // index.jsx's loadSession repopulates the frame list on every page load, so a condition on the list
  // would drag the user to Map & review every time they reopened a session that already had an output.
  // OUTPUT_CREATED is only ever dispatched from a live tool_end, which a reload does not produce.
  //
  // Never for a foundation session: it has no Map & review tab (see the pipeline header below), so landing
  // there would leave the user on a stage they cannot see a tab for and — now that the stage is read-only —
  // cannot type their way off either. Nothing stops a foundation agent naming a frame `*_output`.
  useEffect(() => {
    if (outputCreatedCounter === 0 || isFoundation) return;
    setStage(STAGE_LIVE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputCreatedCounter, isFoundation]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") clearSelection();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [clearSelection]);

  const goToPage = useCallback(
    (page) => {
      setPage(page);
      loadData(page);
    },
    [setPage, loadData]
  );

  const commitPageJump = useCallback(() => {
    const parsed = parseInt(pageInput, 10);
    if (Number.isNaN(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), totalPages);
    if (clamped !== currentPage) goToPage(clamped);
    else setPageInput(String(currentPage));
  }, [pageInput, currentPage, totalPages, goToPage]);

  const selectionSummary = (() => {
    if (selectionMode === "columns" && selectedColumns.length > 0) {
      const names = tableData
        ? selectedColumns.map((i) => tableData.columns[i]).join(", ")
        : selectedColumns.join(", ");
      return `${selectedColumns.length} column${selectedColumns.length > 1 ? "s" : ""} selected: ${names}`;
    }
    if (selectionMode === "rows" && selectedRows.length > 0) {
      return `${selectedRows.length} row${selectedRows.length > 1 ? "s" : ""} selected`;
    }
    if (selectionMode === "cells" && selectedCells.length > 0) {
      return `${selectedCells.length} cell${selectedCells.length > 1 ? "s" : ""} selected`;
    }
    return null;
  })();

  // Shared table + pagination body, reused by the Working (sample) and Live
  // (mapped_output) views so both render identical data/paging behaviour.
  const renderTableBody = () => {
    if (!tableData) {
      return (
        <Flex flex="1" align="center" justify="center" color="gray.500">
          <Text fontSize="13px">Select a DataFrame to view.</Text>
        </Flex>
      );
    }
    return (
      <>
        {showCoverage ? (
          <FoundationCoverageStrip
            sessionId={sessionId}
            dfKey={selectedKey}
            fingerprint={selectedFrame?.fingerprint}
          />
        ) : null}
        <Box
          flex="1"
          minH={0}
          overflow="auto"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          sx={{
            "&::-webkit-scrollbar": { width: "6px", height: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <DataPreviewTable
            columns={tableData.columns}
            rows={tableData.rows}
            selectedColumns={selectedColumns}
            selectedRows={selectedRows}
            selectedCells={selectedCells}
            onColumnClick={stage === STAGE_WORKING ? selectColumn : undefined}
            onRowClick={stage === STAGE_WORKING ? selectRow : undefined}
            onCellClick={stage === STAGE_WORKING ? selectCell : undefined}
          />
        </Box>

        {totalPages > 1 && (
          <HStack justify="space-between" flexShrink={0} pt={1}>
            <IconButton
              aria-label="Previous page"
              icon={<CaretLeft size={14} weight="bold" />}
              size="sm"
              variant="outline"
              borderRadius="full"
              borderColor="gray.300"
              color="gray.600"
              isDisabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
              _hover={{ bg: "gray.50" }}
            />
            <HStack spacing={2} fontSize="13px" color="gray.600">
              <Text>Page</Text>
              <Input
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                isDisabled={isLoadingData}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                onBlur={commitPageJump}
                size="sm"
                w="64px"
                textAlign="center"
                borderRadius="md"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
                _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
              />
              <Text>of <Text as="span" fontWeight="700" color="gray.800">{totalPages}</Text></Text>
              {tableData.total_rows > 0 && (
                <Text color="gray.400" pl={2}>· {tableData.total_rows.toLocaleString()} rows</Text>
              )}
            </HStack>
            <IconButton
              aria-label="Next page"
              icon={<CaretRight size={14} weight="bold" />}
              size="sm"
              variant="outline"
              borderRadius="full"
              borderColor="gray.300"
              color="gray.600"
              isDisabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
              _hover={{ bg: "gray.50" }}
            />
          </HStack>
        )}
      </>
    );
  };

  return (
    <Flex
      direction="column"
      h="100%"
      w="100%"
      minW={0}
      bg="white"
    >
      {/* Header — eyebrow+title blocks act as stage tabs. Active block is
          full colour, inactive blocks are dimmed. Action chips sit on the
          right, scoped per stage (upload/import on Source, nothing extra on
          Working). No border here: the other stages already put a gap (and
          their own bordered box) between this header and their content, so a
          permanent line here would be a second, redundant border on those
          tabs. Only Preview/Commit's content (the shared commit stage) sits
          flush against this header with no gap, so only those two get the
          full-width line the design calls for. */}
      <Box
        flexShrink={0}
        borderBottom={stage === STAGE_COMMIT || isMappedStage ? "1px solid" : "none"}
        borderColor="gray.200"
      >
        <Flex px={8} pt={8} pb={5} justify="space-between" align="flex-end" flexWrap="wrap" gap={6}>
          <HStack spacing={6} align="flex-end">
            <HStack spacing={0} align="center">
              <StageTabHeader
                icon={FileText}
                eyebrow="Source data"
                label="Upload & view"
                ariaLabel="Upload & view — source data, read only"
                isActive={stage === STAGE_SOURCE}
                onClick={() => setStage(STAGE_SOURCE)}
              />
              <Box pr="22px" color={NAV_CHEVRON_STAGE}>
                <CaretDoubleRight size={17} weight="regular" />
              </Box>
              <StageTabHeader
                icon={PencilSimple}
                eyebrow="Working data"
                label="Transform"
                ariaLabel="Transform — working data, editable"
                isActive={stage === STAGE_WORKING}
                isDisabled={!hasSourceData}
                disabledReason="Upload source data first."
                onClick={() => setStage(STAGE_WORKING)}
              />
              {/* A foundation session's destination has the same shape as its source, so there is no
                  mapping to review — "Map & review" would be a stage with nothing in it. Its pipeline is
                  Source -> Working -> Save. */}
              {!isFoundation && (
                <>
                  <Box pr="22px" color={NAV_CHEVRON_STAGE}>
                    <CaretDoubleRight size={17} weight="regular" />
                  </Box>
                  <StageTabHeader
                    icon={Eye}
                    eyebrow="Mapped data"
                    label="Preview"
                    ariaLabel="Preview — mapped data, read only"
                    isActive={stage === STAGE_LIVE}
                    isDisabled={!hasSourceData}
                    disabledReason="Upload source data first."
                    onClick={() => setStage(STAGE_LIVE)}
                  />
                </>
              )}
              {/* The chevron into Commit/Save is deliberately darker than the others — the only
                  structural cue that what follows is different in kind: a decision, not a state of
                  the data. Commit/Save itself never turns lime like an active data stage (that
                  colour is reserved for Upload file's routine action) — it's dark teal when ready,
                  a dedicated grey when not. Foundation's "Save" gets the same commit-like treatment
                  for the same reason (it's that flow's own final, irreversible action) but keeps its
                  own eyebrow ("Foundation year") — the button-only anatomy still has room for it. */}
              <Box pr="22px" color={NAV_CHEVRON_COMMIT}>
                <CaretDoubleRight size={17} weight="regular" />
              </Box>
              {isFoundation ? (
                <StageTabHeader
                  variant="commit"
                  icon={FlagCheckered}
                  eyebrow="Foundation year"
                  label="Save"
                  ariaLabel="Save — foundation year, writes the curated data to the round"
                  isActive={stage === STAGE_COMMIT}
                  isDisabled={!hasSourceData}
                  disabledReason="Upload source data first."
                  onClick={() => setStage(STAGE_COMMIT)}
                />
              ) : (
                <StageTabHeader
                  variant="commit"
                  icon={FlagCheckered}
                  eyebrow="Platform data"
                  label="Commit"
                  ariaLabel="Commit — platform data, writes the mapped data to the task"
                  isActive={stage === STAGE_COMMIT}
                  // Committing writes `selectedOutputKey` — a mapped "_output" frame — so there is
                  // nothing to commit (and nothing for this tab to show) until Map & review has actually
                  // produced one, not just once a file has been uploaded. Also disabled once this exact
                  // output is already up to date — there is nothing new to commit (brief §4).
                  isDisabled={!hasSourceData || outputDataframes.length === 0 || isCommitUpToDate}
                  disabledReason={
                    !hasSourceData
                      ? "Upload source data first."
                      : isCommitUpToDate
                      ? "Everything in this session is already committed."
                      : "Map your data in Preview first."
                  }
                  chip={commitNavChip}
                  onClick={() => setStage(STAGE_COMMIT)}
                />
              )}
            </HStack>
            {/* Charts is not a pipeline stage — it sits after a plain divider
                (no CaretDoubleRight) and renders as a pill, not an
                eyebrow/title block, so it never reads as a 5th stage in the
                Source -> Working -> Live -> Platform flow. */}
            {SHOW_CHARTS && (
              <>
                <Box alignSelf="flex-end" w="1px" h="28px" bg="gray.200" mb="6px" />
                <ChartsTabHeader
                  isActive={stage === STAGE_CHARTS}
                  count={charts.length}
                  onClick={() => setStage(STAGE_CHARTS)}
                />
              </>
            )}
          </HStack>
          <HStack spacing={2} flexWrap="wrap" justify="flex-end">
            {/* Visible only on the Upload & view stage — that's where a file goes in, whether this
                session has none yet (the EmptyState dropzone handles that case too) or already has
                one and the user wants to add/replace it. */}
            {stage === STAGE_SOURCE && onOpenUpload && (
              <Button
                size="sm"
                bg="limeShade.600"
                color="white"
                borderRadius="full"
                leftIcon={<FileArrowUp size={14} />}
                _hover={{ bg: "limeShade.700" }}
                onClick={onOpenUpload}
              >
                Upload file
              </Button>
            )}
            {/* The stage runs the mapping by itself on arrival, and again whenever the working data has
                moved since — so this is for the cases it deliberately does not: a session reopened with an
                output already in it, a turn that failed, or simply wanting it done again. Without it a
                read-only stage with an automatic trigger has no way to retry at all.
                Disabled while disconnected as well as mid-turn: `sendAutoMessage` queues silently on a
                socket that is not open, so the click would look like nothing happened. */}
            {stage === STAGE_LIVE && !isFoundation && onRemap && (
              <PanelActionButton
                icon={MagicWand}
                label="Re-map with AI"
                onClick={onRemap}
                isDisabled={!isConnected || isTurnActive}
              />
            )}
            {SHOW_CHARTS && stage === STAGE_CHARTS && charts.length > 0 && (
              <PanelActionButton icon={ArrowsClockwise} label="Refresh" onClick={refreshCharts} isDisabled={isLoadingCharts} />
            )}
            {/* Styled after the Report Doc editor's own "History" menu item (ReportDocEditorPanel.jsx)
                — same icon, same plain (no pill/border) treatment. Opens the same right sidebar tray,
                showing the same two documents Commit shows (Methodology + full trail), fetched live from
                GET /trail rather than from a commit payload — see WorkingDataHistoryPanel. */}
            {stage === STAGE_WORKING && (
              <Button
                size="sm"
                variant="ghost"
                color="gray.700"
                fontWeight="500"
                leftIcon={<ClockCounterClockwise size={16} />}
                _hover={{ bg: "gray.100" }}
                onClick={() => {
                  setRightSidebar(
                    <WorkingDataHistoryPanel sessionId={sessionId} sourceFileName={sourceFileName} />
                  );
                  doOpen();
                }}
              >
                History
              </Button>
            )}
            {/* Same slot, same styling, on the Preview and Commit stages (which share a body) — mocked for
                now (CommitHistoryPanel has no real query behind it yet; the wiring is a placeholder for the
                task's past-commit history). */}
            {isMappedStage && (
              <Button
                size="sm"
                variant="ghost"
                color="gray.700"
                fontWeight="500"
                leftIcon={<ClockCounterClockwise size={16} />}
                _hover={{ bg: "gray.100" }}
                onClick={() => {
                  setRightSidebar(<CommitHistoryPanel targetLabel={formatTarget(aiIngestTarget)} />);
                  doOpen();
                }}
              >
                History
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Body */}
      <Flex direction="column" flex="1" minH={0} minW={0} px={6} pb={6} gap={4}>
        {SHOW_CHARTS && stage === STAGE_CHARTS ? (
          <ChartsBoardView
            sessionId={sessionId}
            charts={charts}
            isLoadingCharts={isLoadingCharts}
            onDeleted={refreshCharts}
          />
        ) : stage === STAGE_COMMIT && isFoundation ? (
          <FoundationSavePanel />
        ) : isMappedStage ? (
          // Preview and Commit render the same body, from ONE branch so the element is not remounted when
          // the user moves between the two tabs. Once this output is fully committed, CommitStage itself
          // shows the plain "All data committed" view — which is what Preview shows then, since the Commit
          // tab is disabled.
          <MappedStage sessionId={sessionId} outputs={outputDataframes} hasDestination={hasDestination} />
        ) : isLoadingList && workingDataframes.length === 0 ? (
          <LoadingState label={stage === STAGE_SOURCE ? "Loading source data…" : "Loading working data…"} />
        ) : workingDataframes.length === 0 ? (
          <EmptyState isSourceView={stage === STAGE_SOURCE} onUploaded={onUploaded} onOpenLibrary={onOpenLibrary} />
        ) : (
          <>
            {/* DataFrame tabs */}
            {/* `position="relative"` + an absolutely-positioned full-width line, rather than the
                theme's usual border-on-TabList + Tab's `mb: "-1px"` overlap trick (theme/components/
                tabs.js) — that trick only reaches a border on Tab's DIRECT parent, and the "+" button
                sits beside TabList (both wrapped in a plain Flex) rather than inside it, pushing the
                grey line's needed position out a level further than the margin can reach.
                Painting order does the covering instead: this line has NEGATIVE z-index (painted
                first), so the normal-flow content above it — every Tab's own bottom border, including
                the "primary" variant's transparent one on unselected tabs — paints over it. Transparent
                paint doesn't hide anything, so the grey still shows under unselected tabs and the empty
                space beyond the last one; opaque limegreen does hide it, under the selected tab only. */}
            <Flex align="stretch" flexShrink={0} position="relative" zIndex={0}>
              <Box position="absolute" left={0} right={0} bottom={0} h="1px" bg="gray.200" zIndex={-1} />
              <Box
                overflowX="auto"
                minW={0}
                sx={{
                  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
                  "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
                  "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
                  scrollbarWidth: "thin",
                  scrollbarColor: "transparent transparent",
                  "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
                }}
              >
                <Tabs
                  variant="primary"
                  // -1 when a reference frame is showing, so no working tab is highlighted. Clamping to 0
                  // instead would mark the first working tab as selected while the table below displayed
                  // something else entirely.
                  index={
                    referenceSelected
                      ? -1
                      : Math.max(0, workingDataframes.findIndex((d) => d.key === selectedKey))
                  }
                  onChange={(idx) => {
                    const df = workingDataframes[idx];
                    if (df) setSelectedKey(df.key);
                  }}
                  size="sm"
                >
                  <Flex align="center">
                    <TabList borderBottom="none">
                      {workingDataframes.map((df) => (
                        <Tab key={df.key} whiteSpace="nowrap" px={4}>
                          <HStack spacing={2} align="center">
                            <Text fontSize="13px" fontWeight="600">{df.label}</Text>
                            {df.readonly && <Lock size={11} color="#A0AEC0" weight="bold" />}
                            <Badge ml={1} fontSize="10px" variant="subtle" bg="gray.100" color="gray.600" borderRadius="full" px={2}>
                              {df.rows}&times;{df.columns}
                            </Badge>
                          </HStack>
                        </Tab>
                      ))}
                    </TabList>
                    {/* The Excel-style "+" tab — not a real <Tab> (adding one would shift every index
                        the onChange handler above uses to look up workingDataframes), just a plain
                        button that does exactly what the header's "Upload file" button does. */}
                    {onOpenUpload && (
                      <Tooltip label="Add a file" openDelay={300}>
                        <IconButton
                          aria-label="Add a file"
                          icon={<Plus size={14} weight="bold" />}
                          size="sm"
                          variant="ghost"
                          color="gray.500"
                          ml={1}
                          flexShrink={0}
                          borderRadius="md"
                          onClick={onOpenUpload}
                          _hover={{ bg: "gray.100", color: "gray.700" }}
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </Tabs>
              </Box>
            </Flex>

            {isESGAdmin && referenceDataframes.length > 0 && (
              <Box flexShrink={0} px={1}>
                <Button
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  fontWeight="600"
                  leftIcon={
                    showReference
                      ? <CaretDown size={11} weight="bold" />
                      : <CaretRight size={11} weight="bold" />
                  }
                  onClick={() => setShowReference((v) => !v)}
                >
                  {/* Named for what it holds in THIS kind of session. A foundation session's reference
                      frame is the organisation's wanted datapoints, not a task's allowed values, and the
                      carbon wording ("instead of guessing ids") describes a problem it does not have. */}
                  {isFoundation ? "Wanted datapoints" : "Task reference"} (
                  {referenceDataframes.length})
                </Button>
                {showReference && (
                  <>
                    <Text fontSize="11px" color="gray.500" px={2} pb={1}>
                      {isFoundation
                        ? "The datapoints this organisation asked for, with the unit each is expected in. " +
                          "Read-only — the AI joins against these rather than guessing which names you want."
                        : "The destination task's allowed values and constraints, read-only. The AI joins " +
                          "against these instead of guessing ids."}
                    </Text>
                    <Wrap spacing={2} px={2} pb={2}>
                      {referenceDataframes.map((df) => (
                        <WrapItem key={df.key}>
                          <Button
                            size="xs"
                            variant={selectedKey === df.key ? "solid" : "outline"}
                            colorScheme="gray"
                            fontWeight="500"
                            onClick={() => setSelectedKey(df.key)}
                          >
                            <HStack spacing={2} align="center">
                              <Lock size={10} color="#A0AEC0" weight="bold" />
                              <Text fontSize="12px">{df.label}</Text>
                              <Badge
                                fontSize="9px"
                                variant="subtle"
                                bg="gray.100"
                                color="gray.600"
                                borderRadius="full"
                                px={2}
                              >
                                {df.rows}&times;{df.columns}
                              </Badge>
                            </HStack>
                          </Button>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </>
                )}
              </Box>
            )}

            {stage === STAGE_WORKING && selectionSummary && (
              <HStack flexShrink={0} justify="space-between" px={1}>
                <Tag size="sm" bg="lightLime" color="limeShade.700" border="1px solid" borderColor="lime" borderRadius="full" px={3}>
                  <Sparkle size={11} weight="fill" color="#85C22A" style={{ marginRight: 6 }} />
                  {selectionSummary}
                </Tag>
                <Button size="xs" variant="ghost" color="gray.500" onClick={clearSelection}>
                  Clear
                </Button>
              </HStack>
            )}

            {isLoadingData ? <LoadingState label="Loading data…" /> : renderTableBody()}
          </>
        )}
      </Flex>
    </Flex>
  );
}

const PanelActionButton = ({ icon: Icon, label, onClick, isDisabled, isLoading }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap={2}
    px={3}
    py={1.5}
    borderRadius="full"
    border="1px solid"
    borderColor="gray.200"
    bg="white"
    fontSize="12px"
    fontWeight="600"
    color="gray.700"
    transition="all 0.15s"
    _hover={isDisabled ? undefined : { borderColor: "gray.300", bg: "gray.50" }}
    opacity={isDisabled ? 0.5 : 1}
    cursor={isDisabled ? "not-allowed" : "pointer"}
    aria-disabled={isDisabled}
  >
    {isLoading ? <Spinner size="xs" /> : <Icon size={13} weight="bold" />}
    <Text>{label}</Text>
  </Flex>
);

const LoadingState = ({ label }) => (
  <Flex flex="1" align="center" justify="center" color="gray.500" gap={2}>
    <Spinner size="sm" />
    <Text fontSize="13px">{label}</Text>
  </Flex>
);

// The source empty state doubles as its own upload zone — drag a file onto it or click it, no need to
// find the "Upload file" button above first. The non-source (Working data) empty state has no file of
// its own to accept, so it stays a plain message.
const EmptyState = ({ isSourceView, onUploaded, onOpenLibrary }) => {
  const { handleFiles } = useFileUpload({ onUploaded });
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  if (!isSourceView) {
    return (
      <Flex flex="1" align="center" justify="center" direction="column" gap={3} py={20} color="gray.500">
        <Box w="56px" h="56px" borderRadius="xl" bg="lightLime" display="flex" alignItems="center" justifyContent="center">
          <Sparkle size={26} color="#85C22A" weight="fill" />
        </Box>
        <Text fontSize="20px" fontWeight="700" color="darkTeal">
          Your data will appear here
        </Text>
        <Text fontSize="13px" color="gray.500" textAlign="center" maxW="44ch">
          Upload source data first, then profile, normalise and clean it here in the Working data stage so
          it&apos;s ready to map for import.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex flex="1" align="center" justify="center" direction="column" gap={3} py={16} px={8}>
      <Box w="56px" h="56px" borderRadius="xl" bg="lightLime" display="flex" alignItems="center" justifyContent="center">
        <FileArrowUp size={26} color="#85C22A" weight="fill" />
      </Box>
      <Text fontSize="20px" fontWeight="700" color="darkTeal">
        Add your source data
      </Text>
      <Text fontSize="13px" color="gray.500" textAlign="center" maxW="44ch">
        Drag a CSV or Excel file below, or click to browse.
      </Text>

      <Box
        mt={2}
        w="100%"
        maxW="420px"
        borderWidth="2px"
        borderStyle="dashed"
        borderRadius="lg"
        p={6}
        textAlign="center"
        cursor="pointer"
        borderColor={dragging ? "lime" : "gray.300"}
        bg={dragging ? "lightLime" : "transparent"}
        _hover={{ borderColor: "lime", bg: "lightLime" }}
        transition="all 0.15s"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <Text fontSize="13px" fontWeight="600" color={dragging ? "limeShade.700" : "gray.600"}>
          {dragging ? "Drop the file here…" : "Drag & drop a file here, or click to browse"}
        </Text>
        <Text fontSize="11px" color="gray.400" mt={1}>
          Supports .csv, .xlsx, .xls (max 100MB)
        </Text>
      </Box>

      {onOpenLibrary && (
        <Text
          as="button"
          type="button"
          fontSize="12px"
          color="limeShade.600"
          fontWeight="600"
          onClick={onOpenLibrary}
          _hover={{ textDecoration: "underline" }}
        >
          or choose from your file library
        </Text>
      )}
    </Flex>
  );
};

// Chakra's bg/color props resolve a theme token name ("lime", "darkTeal", "limeShade.600")
// directly, but the phosphor icon components are plain SVG props, not theme-aware — a bare
// "lime" there would resolve to the CSS keyword lime (#00FF00), not this app's brand lime. Wrap
// every token name for icon color through this; hex values (no theme equivalent given in the
// spec) pass through unchanged.
const cssColor = (value) => (value.startsWith("#") ? value : `var(--chakra-colors-${value.replace(".", "-")})`);

// Colours with no equivalent theme token (per the nav spec's Tokens table) — kept literal.
const NAV_INACTIVE_TILE_BG = "#F1F4F6";
const NAV_INACTIVE_GLYPH = "#6B7780";
const NAV_INACTIVE_LABEL = "#87929B";
const NAV_EYEBROW_STAGE = "#8A98A5";
const NAV_EYEBROW_COMMIT = "#4E6068";
const NAV_CHEVRON_STAGE = "#C9D3D8";
const NAV_CHEVRON_COMMIT = "#7F8E95";
const NAV_COMMIT_DISABLED_BG = "#DCE4E8";
const NAV_COMMIT_DISABLED_TEXT = "#8A98A5";

// One anatomy for all four pipeline stages: an icon tile (colour is the only thing that changes
// meaning) beside an eyebrow (names the DATA) over a label (names the TASK). `variant="commit"`
// is the one that differs in kind — it never turns lime like an active data stage, because commit
// is a decision, not a state to look at; it's dark teal when ready and a dedicated grey when not,
// so the irreversible action never shares a colour with a routine one (Upload file's lime).
const StageTabHeader = ({ icon: Icon, eyebrow, label, isActive, isDisabled, disabledReason, onClick, variant = "default", ariaLabel, chip }) => {
  const isCommit = variant === "commit";

  let tileBg;
  let glyphColor;
  let labelColor;
  let labelWeight;
  if (isCommit) {
    tileBg = isDisabled ? NAV_COMMIT_DISABLED_BG : "darkTeal";
    glyphColor = isDisabled ? NAV_COMMIT_DISABLED_TEXT : "white";
    labelColor = isDisabled ? NAV_COMMIT_DISABLED_TEXT : "darkTeal";
    labelWeight = 800;
  } else if (isActive) {
    tileBg = "lime";
    glyphColor = "pureBlack";
    labelColor = "limeShade.600";
    labelWeight = 800;
  } else {
    tileBg = NAV_INACTIVE_TILE_BG;
    glyphColor = NAV_INACTIVE_GLYPH;
    labelColor = NAV_INACTIVE_LABEL;
    labelWeight = 700;
  }

  return (
    <Tooltip label={isDisabled ? disabledReason : undefined} isDisabled={!isDisabled} openDelay={200}>
      <Flex
        as="button"
        type="button"
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
        aria-current={isActive ? "step" : undefined}
        aria-label={ariaLabel}
        align="center"
        gap="10px"
        pr="22px"
        cursor={isDisabled ? "not-allowed" : isActive ? "default" : "pointer"}
        _hover={isDisabled || isActive ? undefined : { opacity: 0.85 }}
        transition="opacity 0.15s"
      >
        <Flex w="30px" h="30px" flexShrink={0} borderRadius="8px" bg={tileBg} align="center" justify="center">
          {Icon && <Icon size={16} weight="regular" color={cssColor(glyphColor)} />}
        </Flex>
        <Flex direction="column" gap="6px" textAlign="left">
          <Text
            fontSize="10.5px"
            fontWeight="800"
            letterSpacing="0.09em"
            textTransform="uppercase"
            color={isCommit ? NAV_EYEBROW_COMMIT : NAV_EYEBROW_STAGE}
          >
            {eyebrow}
          </Text>
          <HStack spacing={2} align="center">
            <Text fontSize="21px" fontWeight={labelWeight} letterSpacing="-0.5px" color={labelColor} lineHeight="1">
              {label}
            </Text>
            {chip === "upToDate" && (
              <Box
                as="span"
                fontSize="10px"
                fontWeight="700"
                color="gray.500"
                bg="gray.100"
                borderRadius="full"
                px="8px"
                py="2px"
                whiteSpace="nowrap"
              >
                ✓ Up to date
              </Box>
            )}
            {chip === "changed" && (
              <Box
                as="span"
                fontSize="10px"
                fontWeight="700"
                color="limeShade.700"
                bg="lightLime"
                borderRadius="full"
                px="8px"
                py="2px"
                whiteSpace="nowrap"
              >
                Changed
              </Box>
            )}
          </HStack>
        </Flex>
      </Flex>
    </Tooltip>
  );
};

// Deliberately a pill, not an eyebrow/title block — the visual difference
// from ViewTabHeader is what tells the user Charts isn't a 5th pipeline
// stage, just an accumulating board that sits alongside it.
const ChartsTabHeader = ({ isActive, count, onClick }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap={2}
    pb="6px"
    cursor={isActive ? "default" : "pointer"}
    opacity={isActive ? 1 : 0.55}
    _hover={isActive ? undefined : { opacity: 0.85 }}
    transition="opacity 0.15s"
  >
    <ChartBar size={18} weight="bold" color={isActive ? "#5C8A1E" : "#718096"} />
    <Text
      fontSize="14px"
      fontWeight="700"
      color={isActive ? "limeShade.700" : "darkTeal"}
    >
      Charts
    </Text>
    {count > 0 && (
      <Box
        as="span"
        fontSize="11px"
        fontWeight="700"
        color={isActive ? "limeShade.700" : "gray.500"}
        bg={isActive ? "lightLime" : "gray.100"}
        borderRadius="full"
        px={2}
        py="1px"
      >
        {count}
      </Box>
    )}
  </Flex>
);

// The body of BOTH the Preview ("Mapped data") and Commit stages of a carbon session. They used to be two
// views of the same output frame — a plain paginated table here, the Ready/Existing/After preview plus the
// Commit rail there — and the user was asked to read the rows on one tab and decide about them on the
// other. Now both tabs show the commit stage itself; the tabs stay so the pipeline still reads as
// Preview -> Commit.
//
// One element for both stages (WorkingPanel renders this from a single branch), so switching between the
// two tabs keeps CommitStage mounted and its Add/Replace choice, dry-run result and just-committed rail
// survive the switch instead of resetting.
const MappedStage = ({ sessionId, outputs, hasDestination = true }) => {
  // The chat is hidden on these stages (SessionLayout), so the auto-mapping that runs on arrival at Preview
  // has nowhere else to show it is working — this is the only signal the user gets.
  const { isTurnActive } = useChat();

  if (outputs.length === 0) {
    return (
      <Flex flex="1" align="center" justify="center" direction="column" gap={3} py={20} color="gray.500">
        <Box w="56px" h="56px" borderRadius="xl" bg="lightLime" display="flex" alignItems="center" justifyContent="center">
          {isTurnActive ? <Spinner size="md" color="limeShade.600" /> : <FileCsv size={26} color="#85C22A" weight="fill" />}
        </Box>
        <Text fontSize="20px" fontWeight="700" color="darkTeal">
          {isTurnActive ? "Mapping your data with AI…" : "No mapped outputs yet"}
        </Text>
        {/* Two idle states, because they have different causes. Normally arriving here starts the mapping by
            itself, so an empty stage means the turn has not produced anything yet or it failed. With no
            destination the mapping never starts at all — it could only end in "which task?", and that
            question cannot be answered from here: the chat is not shown on this stage. */}
        <Text fontSize="13px" color="gray.500" textAlign="center" maxW="46ch">
          {isTurnActive
            ? "The mapped rows will appear here, ready to review and commit, as soon as the AI has built them."
            : hasDestination
            ? "The mapping runs by itself when you open this stage. If nothing has appeared, use Re-map with AI above, or go back to Transform and talk it through."
            : "This session has no destination yet. Go back to Transform and agree with the assistant which part of the task this data belongs in — the mapping will run as soon as it knows."}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" flex="1" minH={0} minW={0}>
      {outputs.length > 1 && <OutputFrameTabs outputs={outputs} />}
      {/* A re-map rebuilds the frame in place, so the panel below keeps showing the previous mapping until
          the turn finishes. Say so, or a user who commits mid-turn is committing rows that are about to be
          replaced. */}
      {isTurnActive && (
        <HStack flexShrink={0} spacing={2} mt={3} px={3} py={2} bg="lightLime" borderRadius="md" color="limeShade.700">
          <Spinner size="xs" />
          <Text fontSize="12px" fontWeight="600">
            AI is re-mapping — the preview updates when it finishes.
          </Text>
        </HStack>
      )}
      <CommitStage sessionId={sessionId} />
    </Flex>
  );
};

// The output frame tabs — only rendered for more than one output: a single output has nothing to switch
// between. Shared selection, not local: the Commit rail commits `selectedOutputKey`, so the frame picked
// here is by construction the frame being committed. `selectedOutputKey` is reconciled to a live output in
// WorkingPanel regardless of whether this renders.
const OutputFrameTabs = ({ outputs }) => {
  const { selectedOutputKey, setSelectedOutputKey } = useWorkingPanel();
  return (
    <Box
      overflowX="auto"
      w="100%"
      flexShrink={0}
      borderBottom="1px solid"
      borderColor="gray.200"
      sx={{
        "&::-webkit-scrollbar": { width: "6px", height: "6px" },
        "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
        "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
        scrollbarWidth: "thin",
        scrollbarColor: "transparent transparent",
        "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
      }}
    >
      <Tabs
        variant="primary"
        index={Math.max(0, outputs.findIndex((o) => o.key === selectedOutputKey))}
        onChange={(idx) => {
          const out = outputs[idx];
          if (out) setSelectedOutputKey(out.key);
        }}
        size="sm"
      >
        <TabList borderBottom="none">
          {outputs.map((out) => (
            <Tab key={out.key} whiteSpace="nowrap" px={4}>
              <HStack spacing={2} align="center">
                <FileCsv size={13} weight="fill" color="#85C22A" />
                <Text fontSize="13px" fontWeight="600">{out.label}</Text>
                <Badge ml={1} fontSize="10px" variant="subtle" bg="gray.100" color="gray.600" borderRadius="full" px={2}>
                  {out.rows}&times;{out.columns}
                </Badge>
              </HStack>
            </Tab>
          ))}
        </TabList>
      </Tabs>
    </Box>
  );
};
